import React from 'react';
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import { Add as AddIcon, DeleteOutline as DeleteIcon } from '@mui/icons-material';
import { CustomField } from '../../types';

interface CustomFieldsTabProps {
  customFields: CustomField[];
  onChange: (fields: CustomField[]) => void;
  /** Names of this form's own input fields, to warn when a custom key would be overridden. */
  formFieldNames: Set<string>;
}

const WARNING = '#b54708';

/**
 * Default values for keys on every submission. For each key, later wins:
 * the default here, then the form URL's `?key=` value (if enabled on the row),
 * then the visitor's own answer. The result is saved with the response and
 * sent to the workflow by name.
 */
export const CustomFieldsTab: React.FC<CustomFieldsTabProps> = ({
  customFields,
  onChange,
  formFieldNames,
}) => {
  const update = (index: number, patch: Partial<CustomField>) =>
    onChange(customFields.map((row, i) => (i === index ? { ...row, ...patch } : row)));

  return (
    <Box sx={{ maxWidth: 720 }}>
      <Typography sx={{ fontSize: 13, color: 'text.secondary'}}>
        Default values added to every submission of this form. They are saved with the response
        and, if workflow creation is on, sent to the workflow by name.
      </Typography>
      <Box sx={{ mb: 1 }} />
      <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
        For each key the value used is: <b>default</b> → replaced by the <b>URL query value</b>{' '}
        (if enabled below) → replaced by the <b>visitor's answer</b> to a form field with the
        same name (if they fill it in).
      </Typography>
      <Box sx={{ mb: 2.5 }} />

      {customFields.length === 0 && (
        <Typography sx={{ py: 2, color: 'text.secondary', fontSize: 13 }}>
          No custom fields yet.
        </Typography>
      )}

      {customFields.map((row, index) => {
        const key = row.key.trim();
        return (
          <Box key={index} sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
              <TextField
                size="small"
                label="Key"
                placeholder="e.g. smtpChannelId"
                value={row.key}
                onChange={(e) => update(index, { key: e.target.value })}
                sx={{ flex: 1 }}
              />
              <TextField
                size="small"
                label="Default value"
                value={row.value}
                onChange={(e) => update(index, { value: e.target.value })}
                sx={{ flex: 1.4 }}
              />
              <IconButton
                aria-label="Remove custom field"
                onClick={() => onChange(customFields.filter((_, i) => i !== index))}
                sx={{ mt: 0.25 }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>

            <FormControlLabel
              sx={{ ml: 0.25, mt: 0.25 }}
              control={
                <Checkbox
                  size="small"
                  checked={Boolean(row.fromQuery)}
                  onChange={(e) => update(index, { fromQuery: e.target.checked })}
                />
              }
              label={
                <Typography sx={{ fontSize: 12.5 }}>
                  Take from the URL when present
                  {key && (
                    <>
                      {' '}
                      (<code>?{key}=…</code>)
                    </>
                  )}
                </Typography>
              }
            />

            {key && formFieldNames.has(key) && (
              <Typography sx={{ fontSize: 12, color: WARNING, mt: 0.25 }}>
                A field named “{key}” is in this form. When the visitor fills it in, their answer
                overrides this value.
              </Typography>
            )}
          </Box>
        );
      })}

      <Button
        startIcon={<AddIcon />}
        onClick={() => onChange([...customFields, { key: '', value: '' }])}
        sx={{ textTransform: 'none', mt: 0.5 }}
      >
        Add custom field
      </Button>
    </Box>
  );
};

export default CustomFieldsTab;
